// Minimal PDF 1.4 generator — 외부 의존성 없음
// Korean text: CIDFont + Identity-H + UTF-16BE hex encoding

import type {
  HWPXDocument,
  Section,
  Paragraph,
  Run,
  TextRun,
  TableRun,
  Table,
  CharProperty,
} from '../../types.js';

export interface PdfOptions {
  /** 한글 폰트명 (기본: 'Malgun Gothic'). PDF 뷰어에 설치된 폰트 */
  fontName?: string;
  /** Latin 폰트명 (기본: 'Helvetica') */
  latinFontName?: string;
}

// ─── PDF Object Builder ───

class PdfBuilder {
  objects: string[] = [];
  private offsets: number[] = [];
  private output = '';

  nextId(): number {
    return this.objects.length + 1;
  }

  addObject(content: string): number {
    const id = this.nextId();
    this.objects.push(content);
    return id;
  }

  build(): Uint8Array {
    // Header
    this.output = '%PDF-1.4\n%\xC0\xC1\xC2\xC3\n';

    // Write objects
    for (let i = 0; i < this.objects.length; i++) {
      this.offsets.push(this.output.length);
      this.output += `${i + 1} 0 obj\n${this.objects[i]}\nendobj\n`;
    }

    // Xref
    const xrefOffset = this.output.length;
    this.output += 'xref\n';
    this.output += `0 ${this.objects.length + 1}\n`;
    this.output += '0000000000 65535 f \n';
    for (const off of this.offsets) {
      this.output += off.toString().padStart(10, '0') + ' 00000 n \n';
    }

    // Trailer
    this.output += 'trailer\n';
    this.output += `<< /Size ${this.objects.length + 1} /Root 1 0 R >>\n`;
    this.output += 'startxref\n';
    this.output += `${xrefOffset}\n`;
    this.output += '%%EOF\n';

    return stringToBytes(this.output);
  }
}

// ─── Main Export ───

export function writePdf(doc: HWPXDocument, opts?: PdfOptions): Uint8Array {
  const korFont = opts?.fontName || 'Malgun Gothic';
  const latFont = opts?.latinFontName || 'Helvetica';
  const pdf = new PdfBuilder();

  // Pre-reserve catalog(1), pages(2)
  const catalogId = pdf.addObject(''); // placeholder
  const pagesId = pdf.addObject('');   // placeholder

  // CIDFont for Korean (Type0 + CIDFontType2)
  const cidFontId = pdf.addObject(
    `<< /Type /Font /Subtype /CIDFontType2 /BaseFont /${sanitizeName(korFont)}` +
    ` /CIDSystemInfo << /Registry (Adobe) /Ordering (Identity) /Supplement 0 >>` +
    ` /DW 1000 >>`,
  );

  const korFontId = pdf.addObject(
    `<< /Type /Font /Subtype /Type0 /BaseFont /${sanitizeName(korFont)}` +
    ` /Encoding /Identity-H` +
    ` /DescendantFonts [${cidFontId} 0 R]` +
    ` /ToUnicode ${pdf.nextId() + 1} 0 R >>`,
  );

  // ToUnicode CMap
  const cmapStream = buildToUnicodeCMap();
  const cmapId = pdf.addObject(
    `<< /Length ${cmapStream.length} >>\nstream\n${cmapStream}\nendstream`,
  );

  // Update korFontId ToUnicode ref
  pdf.objects[korFontId - 1] =
    `<< /Type /Font /Subtype /Type0 /BaseFont /${sanitizeName(korFont)}` +
    ` /Encoding /Identity-H` +
    ` /DescendantFonts [${cidFontId} 0 R]` +
    ` /ToUnicode ${cmapId} 0 R >>`;

  // Latin font (standard)
  const latFontId = pdf.addObject(
    `<< /Type /Font /Subtype /Type1 /BaseFont /${sanitizeName(latFont)} /Encoding /WinAnsiEncoding >>`,
  );

  // Font resource dict
  const fontResources = `/F1 ${korFontId} 0 R /F2 ${latFontId} 0 R`;

  // Generate pages
  const pageIds: number[] = [];

  for (const section of doc.sections) {
    const pageW = section.def.pageWidth / 7200 * 72; // HWPUNIT → pt
    const pageH = section.def.pageHeight / 7200 * 72;
    const ml = section.def.pageMargin.left / 7200 * 72;
    const mr = section.def.pageMargin.right / 7200 * 72;
    const mt = section.def.pageMargin.top / 7200 * 72;
    const mb = section.def.pageMargin.bottom / 7200 * 72;

    const bodyW = pageW - ml - mr;
    const bodyH = pageH - mt - mb;

    const ctx: RenderContext = {
      doc,
      pdf,
      pagesId,
      fontResources,
      pageW,
      pageH,
      ml,
      mr,
      mt,
      mb,
      bodyW,
      bodyH,
      pageIds,
    };

    renderSection(ctx, section);
  }

  // Update catalog
  pdf.objects[0] = `<< /Type /Catalog /Pages ${pagesId} 0 R >>`;

  // Update pages
  const kids = pageIds.map((id) => `${id} 0 R`).join(' ');
  pdf.objects[1] = `<< /Type /Pages /Kids [${kids}] /Count ${pageIds.length} >>`;

  return pdf.build();
}

// ─── Rendering ───

interface RenderContext {
  doc: HWPXDocument;
  pdf: PdfBuilder;
  pagesId: number;
  fontResources: string;
  pageW: number;
  pageH: number;
  ml: number;
  mr: number;
  mt: number;
  mb: number;
  bodyW: number;
  bodyH: number;
  pageIds: number[];
}

interface PageStream {
  ops: string[];
  y: number;
}

function renderSection(ctx: RenderContext, section: Section): void {
  let page = newPage(ctx);

  for (const para of section.paragraphs) {
    for (const run of para.runs) {
      if (run.t === 'text') {
        const fontSize = getFontSize(ctx.doc, run.charPrIDRef);
        const lineHeight = fontSize * 1.6;

        if (page.y - lineHeight < 0) {
          flushPage(ctx, page);
          page = newPage(ctx);
        }

        if (run.text) {
          const color = getTextColor(ctx.doc, run.charPrIDRef);
          const bold = isBold(ctx.doc, run.charPrIDRef);

          if (color !== '0 0 0') {
            page.ops.push(color + ' rg');
          }

          // Split into lines by bodyW
          const lines = wrapText(run.text, fontSize, ctx.bodyW);
          for (const line of lines) {
            if (page.y - lineHeight < 0) {
              flushPage(ctx, page);
              page = newPage(ctx);
            }
            page.ops.push('BT');
            page.ops.push(`/F1 ${fontSize} Tf`);
            page.ops.push(`${ctx.ml} ${ctx.mt + page.y - lineHeight} Td`);
            page.ops.push(`<${toUtf16Hex(line)}> Tj`);
            page.ops.push('ET');
            page.y -= lineHeight;
          }

          if (color !== '0 0 0') {
            page.ops.push('0 0 0 rg');
          }
        } else {
          // 빈 문단 = 빈 줄
          page.y -= fontSize * 1.6;
        }
      } else if (run.t === 'table') {
        const tableHeight = estimateTableHeight(run.table, ctx.doc);
        if (page.y - tableHeight < 0 && page.y < ctx.bodyH - 10) {
          flushPage(ctx, page);
          page = newPage(ctx);
        }
        renderTable(ctx, page, run.table);
      }
    }
  }

  flushPage(ctx, page);
}

function newPage(ctx: RenderContext): PageStream {
  return { ops: [], y: ctx.bodyH };
}

function flushPage(ctx: RenderContext, page: PageStream): void {
  if (page.ops.length === 0) return;

  const stream = page.ops.join('\n');
  const streamId = ctx.pdf.addObject(
    `<< /Length ${byteLength(stream)} >>\nstream\n${stream}\nendstream`,
  );

  const pageId = ctx.pdf.addObject(
    `<< /Type /Page /Parent ${ctx.pagesId} 0 R` +
    ` /MediaBox [0 0 ${fmt(ctx.pageW)} ${fmt(ctx.pageH)}]` +
    ` /Resources << /Font << ${ctx.fontResources} >> >>` +
    ` /Contents ${streamId} 0 R >>`,
  );

  ctx.pageIds.push(pageId);
}

// ─── Table Rendering ───

function renderTable(ctx: RenderContext, page: PageStream, table: Table): void {
  const tableW = table.width / 7200 * 72;
  const scale = Math.min(1, ctx.bodyW / tableW);
  const startX = ctx.ml;
  const startY = ctx.mt + page.y;

  for (const row of table.rows) {
    const rowH = row.height / 7200 * 72 * scale;
    let cellX = startX;

    for (const cell of row.cells) {
      const cellW = cell.width / 7200 * 72 * scale;
      const cellH = cell.height / 7200 * 72 * scale;
      const cellY = startY - rowH;

      // Border
      page.ops.push('0.5 w');
      page.ops.push(`${fmt(cellX)} ${fmt(cellY)} ${fmt(cellW)} ${fmt(cellH)} re S`);

      // Cell text
      const text = cell.paragraphs
        .flatMap((p) => p.runs.filter((r): r is TextRun => r.t === 'text').map((r) => r.text))
        .join(' ');

      if (text) {
        const fontSize = 8 * scale;
        const textX = cellX + 3;
        const textY = cellY + cellH - fontSize - 2;
        page.ops.push('BT');
        page.ops.push(`/F1 ${fmt(fontSize)} Tf`);
        page.ops.push(`${fmt(textX)} ${fmt(textY)} Td`);
        page.ops.push(`<${toUtf16Hex(text.substring(0, 100))}> Tj`);
        page.ops.push('ET');
      }

      cellX += cellW;
    }

    page.y -= rowH;
    startY === ctx.mt + page.y + rowH; // keep startY updated implicitly via page.y
  }
}

function estimateTableHeight(table: Table, doc: HWPXDocument): number {
  let h = 0;
  for (const row of table.rows) {
    h += row.height / 7200 * 72;
  }
  return h;
}

// ─── Text Helpers ───

function getFontSize(doc: HWPXDocument, charPrIDRef: number): number {
  const cp = doc.head.charProperties.find((c) => c.id === charPrIDRef);
  if (!cp) return 10;
  return cp.height / 100; // charHeight → pt
}

function getTextColor(doc: HWPXDocument, charPrIDRef: number): string {
  const cp = doc.head.charProperties.find((c) => c.id === charPrIDRef);
  if (!cp || cp.textColor === 0) return '0 0 0';
  const r = (cp.textColor & 0xff) / 255;
  const g = ((cp.textColor >> 8) & 0xff) / 255;
  const b = ((cp.textColor >> 16) & 0xff) / 255;
  return `${fmt(r)} ${fmt(g)} ${fmt(b)}`;
}

function isBold(doc: HWPXDocument, charPrIDRef: number): boolean {
  const cp = doc.head.charProperties.find((c) => c.id === charPrIDRef);
  return cp?.bold || false;
}

function wrapText(text: string, fontSize: number, maxWidth: number): string[] {
  // 간단한 줄바꿈: 글자당 폭 추정 (한글 ≈ fontSize, 영문 ≈ fontSize*0.5)
  const lines: string[] = [];
  let current = '';
  let currentWidth = 0;

  for (const ch of text) {
    const w = ch.charCodeAt(0) > 0x7f ? fontSize : fontSize * 0.5;
    if (currentWidth + w > maxWidth && current) {
      lines.push(current);
      current = '';
      currentWidth = 0;
    }
    current += ch;
    currentWidth += w;
  }
  if (current) lines.push(current);

  return lines.length > 0 ? lines : [''];
}

// ─── CMap ───

function buildToUnicodeCMap(): string {
  return [
    '/CIDInit /ProcSet findresource begin',
    '12 dict begin',
    'begincmap',
    '/CIDSystemInfo',
    '<< /Registry (Adobe) /Ordering (UCS) /Supplement 0 >> def',
    '/CMapName /Adobe-Identity-UCS def',
    '/CMapType 2 def',
    '1 begincodespacerange',
    '<0000> <FFFF>',
    'endcodespacerange',
    '1 beginbfrange',
    '<0000> <FFFF> <0000>',
    'endbfrange',
    'endcmap',
    'CMapName currentdict /CMap defineresource pop',
    'end',
    'end',
  ].join('\n');
}

// ─── Utilities ───

function toUtf16Hex(str: string): string {
  let hex = '';
  for (let i = 0; i < str.length; i++) {
    const code = str.charCodeAt(i);
    hex += code.toString(16).padStart(4, '0');
  }
  return hex.toUpperCase();
}

function sanitizeName(name: string): string {
  return name.replace(/[^a-zA-Z0-9]/g, '');
}

function fmt(n: number): string {
  return Number(n.toFixed(2)).toString();
}

function byteLength(str: string): number {
  let len = 0;
  for (let i = 0; i < str.length; i++) {
    const code = str.charCodeAt(i);
    if (code <= 0x7f) len++;
    else if (code <= 0x7ff) len += 2;
    else len += 3;
  }
  return len;
}

function stringToBytes(str: string): Uint8Array {
  const bytes: number[] = [];
  for (let i = 0; i < str.length; i++) {
    const code = str.charCodeAt(i);
    if (code <= 0xff) {
      bytes.push(code);
    } else {
      // UTF-8 encode
      if (code <= 0x7ff) {
        bytes.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
      } else {
        bytes.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
      }
    }
  }
  return new Uint8Array(bytes);
}
